import axios from 'axios';
import { dispatch } from 'store';
import { PaginationInfo, RequestParams } from 'types/pagination';
import { CheckboxItem, EssayProduct, getPermissions, Product, ProductCategory } from 'types/product';

export const getProducts = async (
    setProducts: (val: Product[]) => void,
    requestParams: RequestParams,
    setPaginationInfo?: (val: PaginationInfo) => void
) => {
    try {
        const params: any = {
            page: requestParams.page,
            per_page: requestParams.per_page,
            sort: `${requestParams.sort[0].sort === 'desc' ? '-' : ''}${requestParams.sort[0].field}`
        };
        requestParams.filters?.forEach((filter) => {
            params[`filter[${filter.name}]`] = filter.value;
        });
        const response = await axios.get('/products', { params });

        const productData: Product[] = response.data.products.data;

        let availableFilters: any = [];
        response.data.products.filters.forEach((filter: any) => {
            availableFilters = [...availableFilters, ...filter.items.options];
        });

        setPaginationInfo?.({
            total: response.data.products.total,
            last_page: response.data.products.last_page,
            per_page: response.data.products.per_page,
            sortable_fields: availableFilters.map((a: any) => a.name),
            available_filters: response.data.products.filters
        });

        setProducts(productData);
    } catch (err) {
        console.error(`error in getting products ${err}`);
    }
};

export const disableProduct = async (id: string) => {
    try {
        await axios.delete(`/products/${id}`);
    } catch (err) {
        console.error(`error in disable products ${err}`);
    }
};

export const getProductCategories = async (
    setProductCategories: (val: ProductCategory[]) => void,
    requestParams: RequestParams,
    setPaginationInfo?: (val: PaginationInfo) => void
) => {
    try {
        const params: any = {
            page: requestParams.page,
            per_page: requestParams.per_page,
            sort: `${requestParams.sort[0].sort === 'desc' ? '-' : ''}${requestParams.sort[0].field}`
        };
        requestParams?.filters?.forEach((filter) => {
            params[`filter[${filter.name}]`] = filter.value;
        });
        const response = await axios.get('/product-categories', { params });

        const productCategoriesData: ProductCategory[] = response.data.product_categories.data;

        let availableFilters: any = [];
        response.data.product_categories.filters.forEach((filter: any) => {
            availableFilters = [...availableFilters, ...filter.items.options];
        });

        setPaginationInfo?.({
            total: response.data.product_categories.total,
            last_page: response.data.product_categories.last_page,
            per_page: response.data.product_categories.per_page,
            sortable_fields: availableFilters.map((a: any) => a.name),
            available_filters: response.data.product_categories.filters
        });

        setProductCategories(productCategoriesData);
    } catch (err) {
        console.error(`error in getting product categories ${err}`);
    }
};

export const disableProductCategory = async (id: string) => {
    try {
        await axios.delete(`/product-category/${id}`);
    } catch (err) {
        console.error(`error in disable product category ${err}`);
    }
};

export const createProductCategory = async (product_category: ProductCategory) => {
    const body = {
        name: product_category.name
    };
    const response = await axios.post('/product-category', body);
    return response.status;
};

export const getProductCategory = async (id: string, setCategory: (val: ProductCategory) => void) => {
    try {
        const response = await axios.get(`/product-categories/${id}`);
        setCategory(response.data.product_category);
    } catch (error) {
        console.error(`error in get product category ${error}`);
    }
};
export const getProductBasedCategory = async (id: number, setProduct: (val: Product[]) => void) => {
    try {
        const response = await axios.get(`/product-based-on-category/${id}`);
        setProduct(response.data.product);
    } catch (error) {
        console.error(`error in get product ${error}`);
    }
};
export const getMaterials = async (setMaterials: (val: any) => void) => {
    try {
        const response = await axios.get(`/get-teacher-material-list`);
        setMaterials(response.data.atomicunit);
    } catch (error) {
        console.error(`error in get product category ${error}`);
    }
};
export const updateProductCategory = async (product_category: ProductCategory) => {
    const body = { ...product_category };

    const response = await axios.put(`/product-categories/${product_category.id}`, body);
    return response.status;
};

export const getProductCategoriesList = async (setProductCategories: (value: ProductCategory[]) => void) => {
    try {
        const response = await axios.get('/product-category/categories');

        const categoriesData: ProductCategory[] = [];
        for (const [key, value] of Object.entries(response.data.categories)) {
            categoriesData.push({ id: key, name: value as string });
        }
        setProductCategories(categoriesData);
    } catch (err) {
        console.error(`Error getting categories list ${err}`);
    }
};

export const createProduct = async (product: Product, onSuccess: (val: boolean) => void) => {
    const body = { ...product };

    try {
        await axios.post('products', body);
        onSuccess(true);
    } catch (error) {
        console.error(`error in create product ${error}`);
        onSuccess(false);
    }
};

export const getProduct = async (
    id: string,
    setProduct: (val: Product) => void,
    setExamData: (val: any) => void,
    setProductMeetings: (val: any) => void,
    setProductEssay: (val: EssayProduct[]) => void
) => {
    try {
        const response = await axios.get(`products/${id}`);
        setProduct(response.data.product);
        setExamData(response.data.product.exams);
        setProductMeetings(response.data.product.meetings);
        setProductEssay(response.data.product.essays);
    } catch (error) {
        console.error(`error in create product ${error}`);
    }
};

export const getPermission = async (id: string, setFormData: (val: getPermissions) => void): Promise<void> => {
    try {
        const response = await axios.get(`products/${id}`);
        const data: getPermissions = {
            productId: response.data.product.id,
            permission: response.data.product.menu[0].permission
        };

        setFormData(data);
    } catch (error) {
        console.error(`Error in update permission: ${error}`);
    }
};

export const updateProduct = async (product: Product, onSuccess: (val: boolean) => void) => {
    const body = { ...product };

    try {
        await axios.put(`products/${product.id}`, body);
        onSuccess(true);
    } catch (error) {
        console.error(`error in update product ${error}`);
        onSuccess(false);
    }
};
export const getProductEssayList = async (setProductEssay: (value: EssayProduct[]) => void, name?: string) => {
    try {
        let url = '/get-product-essay-list';
        if (name) {
            url = `/get-product-essay-list?sort=${name}`;
        }
        const response = await axios.get(url);
        const productessaydata = response.data.data;
        setProductEssay(productessaydata);
    } catch (err) {
        console.error(`Error getting categories list ${err}`);
    }
};

export const saveProductPermission = async (formData: getPermissions, onSuccess: (val: boolean) => void) => {
    const body = { ...formData };

    try {
        await axios.post('/save-menu-list', body);
        onSuccess(true);
    } catch (error) {
        console.error(`error in getting permission  ${error}`);
        onSuccess(false);
    }
};

export default getProducts;
